import { FC, useEffect, useState } from "react";
import { parseDate, parseNum, _ } from "../../utils/helpers";
import { Icon } from "../Icon";
import { CalendarDateInfo } from "./Calendar";
import { MonthAvailabilityDate, PriceRuleMatch } from "../../types";
import { findPriceRuleMatches } from "../../utils/helpers";
import { useApp } from "../../context/useApp";
import { MIN_DISCOUNT_TO_DISPLAY_IN_CALENDAR, FORCED_FEW_PLACES_DAYS } from "../../utils/symbols";

interface DateCellProps {
    dateInfo: CalendarDateInfo
    onDateSelected: (date: string) => void
    availability: MonthAvailabilityDate
    disabled?:boolean
    highlighted?: boolean
}

export const DateCell: FC<DateCellProps> = ({availability, onDateSelected, dateInfo, disabled, highlighted}) => {

    const {
        state: {priceRules, booking}
    } = useApp()

    //hide the non selectable cell dates from the voice assistant

    const id = parseDate('yyyy-mm-dd', dateInfo.dateObject)
    const dynamicClassName = `${dateInfo.isWeekend ? 'weekend' : ''} ${dateInfo.isToday ? 'today' : ''} ${dateInfo.isSelected ? 'selected' : ''} ${highlighted ? 'highlighted' : ''}`
    const hiddenCell = {
        'aria-hidden': true,
        'aria-disabled': true,
        // 'tabIndex': -1
    }

    /* -------------------------------------------------------------------
     | INVISIBLE DATE CELL
     -------------------------------------------------------------------- */
    if (dateInfo.isBeforeMonthStart || dateInfo.isAfterMonthEnd) return ( 
        <td 
            id={id} 
            role="cell"
            className="cell hidden disabled day w-100" 
            {...hiddenCell}
        ></td>
    )
    /* -------------------------------------------------------------------
     | DISABLED DATE CELL
     -------------------------------------------------------------------- */
    if (dateInfo.isBeforeToday || disabled || availability === -1) return (
        <td 
            id={id}
            role="cell"
            className={`cell disabled day w-100 h-100 r3 df fd-c jcsb aifs ${dynamicClassName}`}
            {...hiddenCell}
        >
            <span className="day-container h-100  df aifs fd-c jcsb" aria-hidden>
                <p className="text s24-500 left">{parseDate('d',dateInfo.dateObject)}</p>
            </span>
        </td>
    )
    /* -------------------------------------------------------------------
     | SOLD OUT DATE CELL
     -------------------------------------------------------------------- */
    if (availability === 0) return (
        <td 
            id={id}
            role="cell"
            className={`cell disabled day w-100 h-100 r3 df fd-c jcsb aifs ${dynamicClassName}`}
            {...hiddenCell}
        >
            <span className="day-container h-100 df aifs jcsb fd-c" aria-hidden>
                <p className="text s24-500 left">{parseDate('d',dateInfo.dateObject)}</p>
                <Icon icon="circle"/>
            </span>
        </td>
    )

    /* -------------------------------------------------------------------
     | DEFAULT DATE CELL
     -------------------------------------------------------------------- */
    const matches = findPriceRuleMatches({bookingDate: dateInfo.dateObject, journey: booking.journey}, priceRules)
    const dayDiffBetweenTodayAndCellDate = Math.floor(
        (dateInfo.dateObject.getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24)
    ) + 1
    const showFireIcon = availability === 1 || (dayDiffBetweenTodayAndCellDate <= FORCED_FEW_PLACES_DAYS)
    const showDiscountIcon = matches.find(match => match.visible === 1)
    //Ej: on this date: special prices available, few places remain, a special event takes place
    const description = `
        ${(showDiscountIcon || availability === 1 || highlighted) ? `${_('0ar_onthi__')}: ` : ''}
        ${showDiscountIcon  ? `${_('2sh_speci__')}, ` : ''}
        ${availability === 1 ? `${_('0ar_fewpl__')}, `: ''}
        ${highlighted ? `${_('0ar_aspec__')}, ` : ''}
        `
    
        return (
        <td  
            id={id} 
            className={`cell  w-100 h-100 r3 `} 
            role="cell"
        >
            <button 
                aria-selected={dateInfo.isSelected ? "true" : "false"}
                aria-label={`${_('0ar_click__', parseDate('Monday d November yyyy', dateInfo.dateObject))}. ${description}`}
                className={`button b-0 p-0 cell day w-100 h-100 r3 df fd-c jcsb aifs p-r ${dynamicClassName} outline-shadow`}
                onClick={() => onDateSelected(parseDate('yyyy-mm-dd', dateInfo.dateObject))}
           >
                <span className="day-container h-100 df aifs jcsb fd-c" aria-hidden>
                    <p className="text s24-500 left">
                        {parseDate('d',dateInfo.dateObject)}
                    </p>
                    {showFireIcon && (<Icon icon="fire" aria-hidden/>)}
                </span>
                { showDiscountIcon && ( <Icon icon={'discount'} aria-hidden /> )}
            </button>
        </td>
    )
}